import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Table, Thead, Tbody, Tr, Th, Td,
  useToast, IconButton, Switch, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure,
  Input,
} from '@chakra-ui/react';
import { FormControl, FormLabel } from '@chakra-ui/form-control';
import { FiRefreshCw, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';

const API_BASE = '/api/v1';
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Token ${localStorage.getItem('token')}`,
});

export default function BackupWebhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editItem, setEditItem] = useState(null);
  const emptyForm = {
    url: '', secret: '', notify_on_created: true, notify_on_failed: true, is_enabled: true,
  };
  const [form, setForm] = useState({ ...emptyForm });
  const toast = useToast();

  const fetchWebhooks = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/backup-webhooks/`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { setWebhooks(d.results || d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchWebhooks(); }, [fetchWebhooks]);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm }); onOpen(); };
  const openEdit = (w) => {
    setEditItem(w);
    setForm({
      url: w.url, secret: '', notify_on_created: w.notify_on_created,
      notify_on_failed: w.notify_on_failed, is_enabled: w.is_enabled,
    });
    onOpen();
  };

  const save = async () => {
    const body = { ...form };
    if (!body.secret && editItem) delete body.secret;
    const url = editItem ? `${API_BASE}/backup-webhooks/${editItem.id}/` : `${API_BASE}/backup-webhooks/`;
    try {
      const res = await fetch(url, {
        method: editItem ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: editItem ? 'Webhook updated' : 'Webhook created', status: 'success', duration: 3000 });
      onClose();
      fetchWebhooks();
    } catch (err) {
      toast({ title: 'Failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const deleteWebhook = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    await fetch(`${API_BASE}/backup-webhooks/${id}/`, { method: 'DELETE', headers: getAuthHeaders() });
    fetchWebhooks();
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" /></Box>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Backup Webhooks</Heading>
          <Text color="gray.500">Get notified when backups complete or fail</Text>
        </VStack>
        <HStack>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>Add Webhook</Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={fetchWebhooks}>Refresh</Button>
        </HStack>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>URL</Th>
              <Th>Secret</Th>
              <Th>On Created</Th>
              <Th>On Failed</Th>
              <Th>Enabled</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {webhooks.map(w => (
              <Tr key={w.id}>
                <Td fontSize="sm" maxW="300px" isTruncated title={w.url}>{w.url}</Td>
                <Td><Badge colorScheme={w.secret_set ? 'green' : 'gray'}>{w.secret_set ? 'Set' : 'None'}</Badge></Td>
                <Td><Badge colorScheme={w.notify_on_created ? 'green' : 'gray'}>{w.notify_on_created ? 'Yes' : 'No'}</Badge></Td>
                <Td><Badge colorScheme={w.notify_on_failed ? 'green' : 'gray'}>{w.notify_on_failed ? 'Yes' : 'No'}</Badge></Td>
                <Td><Badge colorScheme={w.is_enabled ? 'green' : 'gray'}>{w.is_enabled ? 'Active' : 'Disabled'}</Badge></Td>
                <Td fontSize="xs">{new Date(w.created_at).toLocaleString()}</Td>
                <Td>
                  <HStack spacing={1}>
                    <Tooltip label="Edit">
                      <IconButton size="xs" icon={<FiEdit2 />} onClick={() => openEdit(w)} />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton size="xs" icon={<FiTrash2 />} colorScheme="red" variant="ghost" onClick={() => deleteWebhook(w.id)} />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
            {webhooks.length === 0 && (
              <Tr><Td colSpan={7} textAlign="center" py={6} color="gray.400">No webhooks configured</Td></Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? 'Edit Webhook' : 'Add Backup Webhook'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Webhook URL</FormLabel>
                <Input size="sm" placeholder="https://example.com/webhook"
                  value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Secret (optional)</FormLabel>
                <Input size="sm" type="password"
                  placeholder={editItem?.secret_set ? '••••••••' : 'Shared secret for signatures'}
                  value={form.secret} onChange={e => setForm({ ...form, secret: e.target.value })} />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Sent as X-Webhook-Secret header. HMAC-SHA256 signature in X-Webhook-Signature.
                </Text>
              </FormControl>

              <HStack w="100%" spacing={8}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Notify on Created (success)</FormLabel>
                  <Switch isChecked={form.notify_on_created}
                    onChange={e => setForm({ ...form, notify_on_created: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel fontSize="sm" mb={0}>Notify on Failed</FormLabel>
                  <Switch isChecked={form.notify_on_failed}
                    onChange={e => setForm({ ...form, notify_on_failed: e.target.checked })} />
                </FormControl>
              </HStack>

              <FormControl display="flex" alignItems="center">
                <FormLabel fontSize="sm" mb={0}>Enabled</FormLabel>
                <Switch isChecked={form.is_enabled}
                  onChange={e => setForm({ ...form, is_enabled: e.target.checked })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={save}>{editItem ? 'Update' : 'Create'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
